import React, { useEffect, useState } from "react";
import { Gift, Copy, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral/me").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copiato negli appunti");
      setTimeout(() => setCopied(false), 2000);
    } catch (_err) {
      toast.error("Impossibile copiare");
    }
  };

  const share = async () => {
    const msg = `Unisciti a me su ORIO – la rete sicura per i cani. Usa il mio invito: ${data.share_url}`;
    if (navigator.share) {
      try { await navigator.share({ title: "ORIO", text: msg, url: data.share_url }); } catch (_e) { /* user cancelled */ }
    } else {
      copy(data.share_url);
    }
  };

  return (
    <div data-testid="referral-card"
      className="rounded-3xl p-5 sm:p-6 border relative overflow-hidden orio-card-warm"
    >
      <div aria-hidden className="absolute -right-10 -top-10 h-40 w-40 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,184,60,0.25) 0%, transparent 70%)" }} />

      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">
          <Gift className="h-3 w-3" /> Invita un amico
        </div>
        <h3 className="font-display text-2xl mt-1.5 tracking-tight text-[#1A1A1A]">
          1 mese gratis
          <span className="text-[#9A9A9A] font-normal text-base ml-2">per ogni amico abbonato</span>
        </h3>
        <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
          Quando un amico che hai invitato attiva l&apos;abbonamento mensile, ti regaliamo automaticamente un mese di ORIO.
        </p>

        <div className="mt-5 grid grid-cols-3 gap-2">
          <Stat label="Invitati" value={data.invited_count} color="#FF7A1A" />
          <Stat label="Abbonati" value={data.subscribed_count} color="#2DBA60" />
          <Stat label="Mesi guadagnati" value={(data.rewards || []).length} color="#E94545" />
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A] mb-2 font-medium">Il tuo codice</div>
          <div className="flex items-center gap-2 rounded-2xl bg-white border border-[#F0EBE3] p-2.5">
            <code data-testid="referral-code" className="font-mono text-base flex-1 px-2 text-[#C25612] font-semibold">{data.code}</code>
            <button
              data-testid="referral-copy"
              onClick={() => copy(data.code)}
              className="px-3 py-1.5 rounded-lg bg-[#FBF7F1] hover:bg-[#F4EFE6] text-xs flex items-center gap-1.5 text-[#1A1A1A] font-medium"
            >
              {copied ? <Check className="h-3 w-3 text-[#2DBA60]" /> : <Copy className="h-3 w-3" />}
              Copia
            </button>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            data-testid="referral-share"
            onClick={share}
            className="flex-1 orio-cta-btn px-4 py-3 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2"
          >
            <Share2 className="h-4 w-4" /> Condividi invito
          </button>
          <button
            data-testid="referral-copy-link"
            onClick={() => copy(data.share_url)}
            className="px-5 py-3 rounded-full bg-white border border-[#F0EBE3] text-sm text-[#1A1A1A] font-medium hover:bg-[#F4EFE6]"
          >
            Link
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-2xl bg-white border border-[#F0EBE3] px-3 py-2.5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.04)]">
      <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A] font-medium leading-tight">{label}</div>
      <div className="font-display text-xl mt-1 tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
